import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useCurrency } from '../hooks/useCurrency';

interface ChartData {
  period: string;
  sales: number;
  profit: number;
}

interface SalesChartProps {
  data: ChartData[];
  title: string;
  showProfit?: boolean;
  children?: React.ReactNode;
  loading?: boolean;
}

// Add animation styles
const animationStyles = `
  @keyframes chartPulse {
    0%, 100% { opacity: 0.7; }
    50% { opacity: 0.5; }
  }
  .chart-bar-loading {
    animation: chartPulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = animationStyles;
  document.head.appendChild(styleSheet);
}

const SalesChart: React.FC<SalesChartProps> = ({ 
  data, 
  title, 
  showProfit = false,
  children,
  loading = false
}) => {
  const { theme } = useTheme();
  const { formatCurrency } = useCurrency();

  // Debug logging
  console.log('SalesChart render:', { 
    dataLength: data?.length, 
    shouldScroll: data && data.length > 10,
    title 
  });

  if (!data || data.length === 0) {
    return (
      <div className={`p-6 rounded-2xl border ${
        theme === 'dark'
          ? 'bg-slate-800 border-slate-700'
          : 'bg-white border-gray-200'
      }`}>
        <h3 className={`text-lg font-semibold mb-4 ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>
          {title}
        </h3>
        <div className={`text-center py-8 ${
          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
        }`}>
          No data available
        </div>
      </div>
    );
  }

  const maxValue = Math.max(
    ...data.map(item => showProfit ? Math.max(item.sales, item.profit || 0) : item.sales)
  );
  
  // Use smaller max height to ensure bars fit within container with padding
  // h-40 = 160px, h-52 = 208px, but we need to leave room at top for rounded corners
  const getBarHeight = (value: number) => {
    const maxHeight = 145; // Reduced from 200 to fit within h-40 (160px) with padding
    return Math.max((value / maxValue) * maxHeight, 4); // Minimum 4px height
  };

  return (
    <div className={`p-3 sm:p-4 lg:p-6 rounded-2xl border flex flex-col h-full ${
      theme === 'dark'
        ? 'bg-slate-800 border-slate-700'
        : 'bg-white border-gray-200'
    }`}>
      <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 mb-3 sm:mb-4 lg:mb-6">
        <h3 className={`text-base sm:text-lg font-semibold ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>
          {title}
        </h3>
        {showProfit && (
          <div className="flex items-center space-x-3 sm:space-x-4 text-xs sm:text-sm">
            <div className="flex items-center space-x-1.5 sm:space-x-2">
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-blue-500 rounded-full"></div>
              <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
                Sales
              </span>
            </div>
            <div className="flex items-center space-x-1.5 sm:space-x-2">
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-green-500 rounded-full"></div>
              <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
                Profit
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Chart */}
      <div className="relative flex-1 pt-12">
        {/* Y-axis labels */}
        <div className="absolute left-0 top-12 bottom-8 flex flex-col justify-between text-xs text-gray-400">
          <span className="hidden sm:inline">{formatCurrency(maxValue)}</span>
          <span className="hidden sm:inline">{formatCurrency(maxValue * 0.75)}</span>
          <span className="inline sm:hidden">{formatCurrency(maxValue)}</span>
          <span className="hidden sm:inline">{formatCurrency(maxValue * 0.5)}</span>
          <span className="hidden sm:inline">{formatCurrency(maxValue * 0.25)}</span>
          <span className="inline sm:hidden">{formatCurrency(maxValue * 0.5)}</span>
          <span>{formatCurrency(0)}</span>
        </div>

        {/* Chart area with horizontal scroll */}
        <div className="ml-12 sm:ml-16 pl-2 sm:pl-4 relative">
          {/* Scrollable container for bars */}
          <div 
            className="overflow-x-auto pb-2" 
            style={{ maxWidth: '100%', overflowY: 'visible' }}
            ref={(el) => {
              if (el && data.length > 10) {
                // Auto-scroll to the end (most recent data) when component mounts or data changes
                requestAnimationFrame(() => {
                  el.scrollLeft = el.scrollWidth - el.clientWidth;
                });
              }
              
              if (el) {
                console.log('Scroll container dimensions:', {
                  clientWidth: el.clientWidth,
                  scrollWidth: el.scrollWidth,
                  canScroll: el.scrollWidth > el.clientWidth,
                  dataLength: data.length
                });
              }
            }}
          >
            <div className="relative" style={{ overflowY: 'visible' }}>
              {/* Grid lines */}
              <div className="absolute inset-0 pointer-events-none">
                {[0, 25, 50, 75, 100].map((percentage) => (
                  <div
                    key={percentage}
                    className={`absolute border-b ${
                      theme === 'dark' ? 'border-slate-700' : 'border-gray-100'
                    }`}
                    style={{ 
                      bottom: `${8 + (percentage * 145) / 100}px`,
                      left: '0',
                      right: '0',
                      width: data.length > 10 ? `${data.length * 36}px` : '100%'
                    }}
                  />
                ))}
              </div>

              {/* Chart bars */}
              <div 
                className="flex items-end h-40 sm:h-52 relative z-10"
                style={{ 
                  minWidth: data.length > 10 ? `${data.length * 36}px` : '100%',
                  width: data.length > 10 ? `${data.length * 36}px` : '100%',
                  gap: data.length > 10 ? '8px' : '0',
                  overflowY: 'visible'
                }}
              >
              {loading ? (
                // Show animated skeleton bars during loading with proper animation
                <>
                  {[...Array(data.length > 10 ? data.length : 7)].map((_, idx) => {
                    // Create varying heights for more natural skeleton look
                    const height1 = Math.sin(idx * 0.5) * 50 + 90;
                    const height2 = Math.sin(idx * 0.5 + 2) * 40 + 70;
                    return (
                      <div key={idx} className="flex flex-col items-center space-y-2 h-full justify-end" style={{ 
                        minWidth: data.length > 10 ? '32px' : 'auto',
                        flex: data.length <= 10 ? '1' : 'none'
                      }}>
                        {/* Bar group with sales and profit bars */}
                        <div className="flex items-end justify-center space-x-0.5 sm:space-x-1 w-full">
                          <div 
                            className={`rounded-t-md chart-bar-loading ${
                              theme === 'dark' ? 'bg-blue-500/60' : 'bg-blue-400'
                            }`}
                            style={{ 
                              width: data.length > 10 ? '12px' : '14px',
                              height: `${height1}px`
                            }} 
                          />
                          <div 
                            className={`rounded-t-md chart-bar-loading ${
                              theme === 'dark' ? 'bg-green-500/60' : 'bg-green-400'
                            }`}
                            style={{ 
                              width: data.length > 10 ? '12px' : '14px',
                              height: `${height2}px`,
                              animationDelay: `${(idx * 100) + 100}ms`
                            }} 
                          />
                        </div>
                        {/* X-axis label skeleton */}
                        <div className={`h-3 w-8 rounded-lg animate-pulse ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                      </div>
                    );
                  })}
                </>
              ) : (
                // Show actual data bars
                data.map((item, index) => {
                  const salesHeight = getBarHeight(item.sales);
                  const profitHeight = getBarHeight(item.profit || 0);
                  const maxBarHeight = Math.max(salesHeight, profitHeight);
                  // Show tooltip below if bar is very tall (more than 130px)
                  const showTooltipBelow = maxBarHeight > 130;
                  
                  return (
                  <div 
                    key={index} 
                    className="flex flex-col items-center space-y-2"
                    style={{ 
                      minWidth: data.length > 10 ? '32px' : 'auto',
                      flex: data.length <= 10 ? '1' : 'none'
                    }}
                  >
                    <div className="relative flex items-end justify-center space-x-0.5 sm:space-x-1 w-full">
                      {/* Sales bar */}
                      <div
                        className="bg-blue-500 rounded-t-md transition-all duration-300 hover:bg-blue-600 cursor-pointer group relative"
                        style={{ 
                          height: `${salesHeight}px`,
                          minWidth: data.length > 10 ? '12px' : '14px',
                          width: data.length > 10 ? '12px' : 'auto'
                        }}
                      >
                        {/* Tooltip - positioned dynamically based on bar height */}
                        <div className={`absolute ${showTooltipBelow ? 'top-full mt-2' : 'bottom-full mb-2'} left-1/2 transform -translate-x-1/2 px-2 py-1 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-[100] shadow-lg ${
                          theme === 'dark' 
                            ? 'bg-gray-700 text-white' 
                            : 'bg-gray-800 text-white'
                        }`}>
                          Sales: {formatCurrency(item.sales)}
                          {/* Arrow */}
                          <div className={`absolute left-1/2 transform -translate-x-1/2 ${
                            showTooltipBelow ? 'top-0 -mt-1' : 'bottom-0 -mb-1'
                          }`}>
                            <div className={`w-2 h-2 ${showTooltipBelow ? 'rotate-45' : '-rotate-45'} ${
                              theme === 'dark' ? 'bg-gray-700' : 'bg-gray-800'
                            }`}></div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Profit bar (if enabled) */}
                      {showProfit && (
                        <div
                          className="bg-green-500 rounded-t-md transition-all duration-300 hover:bg-green-600 cursor-pointer group relative"
                          style={{ 
                            height: `${profitHeight}px`,
                            minWidth: data.length > 10 ? '12px' : '14px',
                            width: data.length > 10 ? '12px' : 'auto'
                          }}
                        >
                          {/* Tooltip - positioned dynamically based on bar height */}
                          <div className={`absolute ${showTooltipBelow ? 'top-full mt-2' : 'bottom-full mb-2'} left-1/2 transform -translate-x-1/2 px-2 py-1 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-[100] shadow-lg ${
                            theme === 'dark' 
                              ? 'bg-gray-700 text-white' 
                              : 'bg-gray-800 text-white'
                          }`}>
                            Profit: {formatCurrency(item.profit || 0)}
                            {/* Arrow */}
                            <div className={`absolute left-1/2 transform -translate-x-1/2 ${
                              showTooltipBelow ? 'top-0 -mt-1' : 'bottom-0 -mb-1'
                            }`}>
                              <div className={`w-2 h-2 ${showTooltipBelow ? 'rotate-45' : '-rotate-45'} ${
                                theme === 'dark' ? 'bg-gray-700' : 'bg-gray-800'
                              }`}></div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* X-axis label */}
                    <span className={`text-xs ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`} style={{ fontSize: data.length > 10 ? '9px' : '11px' }}>
                      {item.period}
                    </span>
                  </div>
                );
                })
              )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className={`mt-4 sm:mt-6 pt-3 sm:pt-4 border-t flex flex-col space-y-3 sm:flex-row sm:justify-between sm:space-y-0 ${
        theme === 'dark' ? 'border-slate-700' : 'border-gray-200'
      }`}>
        <div>
          <p className={`text-xs sm:text-sm ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Total Sales
          </p>
          {loading ? (
            <div className={`mt-2 h-6 w-32 rounded-lg animate-pulse ${
              theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
            }`} />
          ) : (
            <p className={`text-lg sm:text-xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              {formatCurrency(data.reduce((sum, item) => sum + item.sales, 0))}
            </p>
          )}
        </div>
        {showProfit && (
          <div>
            <p className={`text-xs sm:text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Total Profit
            </p>
            {loading ? (
              <div className={`mt-2 h-6 w-32 rounded-lg animate-pulse ${
                theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
              }`} />
            ) : (
              <p className={`text-lg sm:text-xl font-bold ${
                theme === 'dark' ? 'text-green-400' : 'text-green-600'
              }`}>
                {formatCurrency(data.reduce((sum, item) => sum + (item.profit || 0), 0))}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Period selector or additional controls */}
      {children && (
        <div className="mt-3 sm:mt-4">
          {children}
        </div>
      )}
    </div>
  );
};

export default SalesChart;